import React, { useMemo } from "react";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Flower,
  Cigarette,
  Cookie,
  Beaker,
  Droplet,
  Container,
  Cannabis,
  Sparkles,
  MapPin,
  Tag,
  X,
} from "lucide-react";

/**
 * UnifiedFilters — the ONE filter component used on Home, Search, and
 * Dispensary Detail. Same visual language everywhere: icon-chip categories,
 * strain-type pills, gauge sliders for THC / Price / Distance, and one
 * in-stock toggle. Props let callers show or hide the distance slider,
 * inject product counts for the chips, or opt into the "{matched} of {total}"
 * header used on the dispensary menu.
 */

const CATEGORIES = [
  { key: "flower",      label: "Flower",      Icon: Flower },
  { key: "vape",        label: "Vape",        Icon: Cigarette },
  { key: "concentrate", label: "Concentrate", Icon: Beaker },
  { key: "edible",      label: "Edible",      Icon: Cookie },
  { key: "pre-roll",    label: "Pre-roll",    Icon: Cannabis },
  { key: "tincture",    label: "Tincture",    Icon: Droplet },
  { key: "topical",     label: "Topical",     Icon: Container },
];

const TYPES = [
  { key: "sativa", label: "Sativa", emoji: "☀️" },
  { key: "hybrid", label: "Hybrid", emoji: "⚖️" },
  { key: "indica", label: "Indica", emoji: "🌙" },
];

// Collapse the many variants POS APIs ship (vapes, vaporizers, Concentrates)
// down to the canonical set we render chips for.
// Canonical size buckets per category — kept in sync with
// backend/public_ingest/size_buckets.py so chip values match server
// `size_bucket` values 1:1. Categories not listed here render no size
// row (e.g., topical/tincture we can add later if patients ask).
const SIZE_BUCKETS = {
  flower:      ["1g", "3.5g", "7g", "14g", "28g"],
  "pre-roll":  ["0.5g", "1g", "2g", "3g", "5g"],
  vape:        ["0.5g", "1g", "2g"],
  concentrate: ["0.5g", "1g", "2g"],
  edible:      ["10mg", "25mg", "50mg", "100mg", "200mg", "500mg"],
  tincture:    ["150mg", "400mg", "750mg", "1500mg"],
  topical:     ["25mg", "100mg", "400mg"],
};

const CATEGORY_ALIASES = {
  flower: "flower",
  vape: "vape",
  vapes: "vape",
  vaporizer: "vape",
  vaporizers: "vape",
  "pre-roll": "pre-roll",
  preroll: "pre-roll",
  "pre roll": "pre-roll",
  prerolls: "pre-roll",
  concentrate: "concentrate",
  concentrates: "concentrate",
  edible: "edible",
  edibles: "edible",
  tincture: "tincture",
  tinctures: "tincture",
  topical: "topical",
  topicals: "topical",
};

const fmtCount = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`);

export const UnifiedFilters = ({
  filters,
  setFilters,
  onReset,
  menu = null, // pass raw product list to auto-count
  catCounts: providedCatCounts = null, // OR pass precomputed counts (Home)
  typeCounts: providedTypeCounts = null,
  brandOptions = null, // [{ name, count }] – provide to show brand dropdown
  showDistance = false,
  showHeader = false,
  total,
  matched,
  testIdPrefix = "unified-filter",
  className = "",
}) => {
  // Auto-count from the menu if provided; otherwise use precomputed counts;
  // otherwise show every category chip.
  const { catCounts, typeCounts, brandChoices } = useMemo(() => {
    if (providedCatCounts) {
      return {
        catCounts: providedCatCounts,
        typeCounts: providedTypeCounts,
        brandChoices: brandOptions,
      };
    }
    if (!menu)
      return {
        catCounts: null,
        typeCounts: null,
        brandChoices: brandOptions,
      };
    const c = {};
    const t = {};
    const b = {};
    for (const p of menu) {
      const raw = (p.category || "").toLowerCase();
      const cat = CATEGORY_ALIASES[raw] || raw;
      if (cat) c[cat] = (c[cat] || 0) + 1;
      const ty = (p.strain_type || "").toLowerCase();
      if (ty) t[ty] = (t[ty] || 0) + 1;
      const br = (p.brand || "").trim();
      if (br) b[br] = (b[br] || 0) + 1;
    }
    // Sort brands by count desc so most-stocked shows first in the picker.
    const derivedBrands = brandOptions
      ? brandOptions
      : Object.entries(b)
          .sort((a, b) => b[1] - a[1])
          .map(([name, count]) => ({ name, count }));
    return { catCounts: c, typeCounts: t, brandChoices: derivedBrands };
  }, [menu, providedCatCounts, providedTypeCounts, brandOptions]);

  const visibleCategories = catCounts
    ? CATEGORIES.filter(({ key }) => (catCounts[key] || 0) > 0)
    : CATEGORIES;
  const visibleTypes = typeCounts
    ? TYPES.filter(({ key }) => (typeCounts[key] || 0) > 0)
    : TYPES;

  const activeCount =
    (filters.category ? 1 : 0) +
    (filters.subcategory ? 1 : 0) +
    (filters.size ? 1 : 0) +
    (filters.type ? 1 : 0) +
    (filters.brand ? 1 : 0) +
    (filters.thc_min ? 1 : 0) +
    (filters.price_max && filters.price_max < 200 ? 1 : 0) +
    (filters.in_stock_only ? 1 : 0) +
    (showDistance && filters.max_distance_mi && filters.max_distance_mi < 250
      ? 1
      : 0);

  const toggle = (key, val) =>
    setFilters((f) => {
      // Changing category invalidates any drilled-in subcategory + size
      // (they're category-specific — 100mg edible + flower is nonsense).
      const next = { ...f, [key]: f[key] === val ? "" : val };
      if (key === "category") {
        next.subcategory = "";
        next.size = "";
      }
      return next;
    });

  return (
    <div
      data-testid={`${testIdPrefix}-root`}
      className={`border border-border bg-card rounded-2xl p-4 sm:p-5 space-y-4 ${className}`}
    >
      {(showHeader || (activeCount > 0 && onReset)) && (
        <div className="flex items-center justify-between gap-3">
          {showHeader ? (
            <div>
              <div className="font-serif text-lg sm:text-xl">
                <span className="text-emerald-300">{matched}</span>
                <span className="text-foreground/50"> of {total}</span>
              </div>
              <div className="text-[11px] uppercase tracking-wider text-foreground/50">
                products matching your filters
              </div>
            </div>
          ) : (
            <div />
          )}
          {activeCount > 0 && onReset && (
            <button
              data-testid={`${testIdPrefix}-reset`}
              onClick={onReset}
              className="inline-flex items-center gap-1 text-xs text-foreground/70 hover:text-foreground border border-border rounded-full px-3 h-8"
            >
              <X className="w-3 h-3" />
              Clear ({activeCount})
            </button>
          )}
        </div>
      )}

      {/* Category icon grid — flex-wrap + justify-center so a partial
          last row (e.g. Tincture / Topical on a 4-col layout) centers
          instead of hugging the left edge. Basis is computed to match
          the previous CSS-grid column widths exactly so nothing else
          in the panel shifts. */}
      {visibleCategories.length > 1 && (() => {
        const cols = Math.min(visibleCategories.length, 4);
        const basis = `calc((100% - ${(cols - 1) * 8}px) / ${cols})`;
        return (
          <div className="flex flex-wrap gap-2 justify-center">
            {visibleCategories.map(({ key, label, Icon }) => {
              const active = filters.category === key;
              const count = catCounts?.[key];
              return (
                <button
                  key={key}
                  type="button"
                  data-testid={`${testIdPrefix}-category-${key}`}
                  onClick={() => toggle("category", key)}
                  style={{ flex: `0 0 ${basis}` }}
                  className={`flex flex-col items-center justify-center gap-1.5 rounded-xl px-2 py-3 transition-all border ${
                    active
                      ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-100"
                      : "bg-background border-border text-foreground/80 hover:border-foreground/40"
                  }`}
                >
                  <Icon className="w-5 h-5" strokeWidth={1.6} />
                  <span className="text-xs font-medium">{label}</span>
                  {typeof count === "number" && (
                    <span className="text-[10px] text-foreground/50 tabular-nums">
                      {fmtCount(count)}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        );
      })()}

      {/* Strain type pills */}
      {visibleTypes.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {visibleTypes.map(({ key, label, emoji }) => {
            const active = filters.type === key;
            const count = typeCounts?.[key];
            return (
              <button
                key={key}
                type="button"
                data-testid={`${testIdPrefix}-type-${key}`}
                onClick={() => toggle("type", key)}
                className={`inline-flex items-center gap-1.5 rounded-full px-4 h-9 text-sm border transition ${
                  active
                    ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-100"
                    : "bg-background border-border text-foreground/80 hover:border-foreground/40"
                }`}
              >
                <span aria-hidden="true">{emoji}</span>
                {label}
                {typeof count === "number" && (
                  <span className="text-[10px] text-foreground/50 tabular-nums">
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Vape subcategory chips — only shown when the user has drilled
          into the Vape category. Toggle AIO ("all-in-one" disposables)
          vs Cart (510-thread cartridges). Skipping either shows every
          vape regardless of subcategory. */}
      {filters.category === "vape" && (
        <div className="flex flex-wrap gap-2">
          {[
            { key: "aio",  label: "AIO / Disposable" },
            { key: "cart", label: "510 Cart" },
          ].map(({ key, label }) => {
            const active = filters.subcategory === key;
            return (
              <button
                key={key}
                type="button"
                data-testid={`${testIdPrefix}-subcategory-${key}`}
                onClick={() =>
                  setFilters((f) => ({
                    ...f,
                    subcategory: f.subcategory === key ? "" : key,
                  }))
                }
                className={`inline-flex items-center gap-1.5 rounded-full px-4 h-9 text-sm border transition ${
                  active
                    ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-100"
                    : "bg-background border-border text-foreground/80 hover:border-foreground/40"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      {/* Size chips — appear when a category is picked. Uses smaller
          pills than the strain-type row so the panel doesn't feel
          crowded. Buckets are canonical (`1g`, `3.5g`, `100mg`…) so
          they match the server's size_bucket value 1:1. */}
      {filters.category && SIZE_BUCKETS[filters.category] && (
        <div className="flex flex-wrap gap-1.5">
          <span className="text-[10px] uppercase tracking-wider text-foreground/50 self-center mr-1">
            Size
          </span>
          {SIZE_BUCKETS[filters.category].map((b) => {
            const active = filters.size === b;
            return (
              <button
                key={b}
                type="button"
                data-testid={`${testIdPrefix}-size-${b}`}
                onClick={() =>
                  setFilters((f) => ({
                    ...f,
                    size: f.size === b ? "" : b,
                  }))
                }
                className={`inline-flex items-center rounded-full px-2.5 h-7 text-[11px] border transition tabular-nums ${
                  active
                    ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-100"
                    : "bg-background border-border text-foreground/75 hover:border-foreground/40"
                }`}
              >
                {b}
              </button>
            );
          })}
        </div>
      )}

      {/* Brand picker — shows a compact select when the parent provides
          a brand list (Home/Search fetch /api/brands, Dispensary derives
          from menu). Hidden entirely when no brands are available. */}
      {brandChoices && brandChoices.length > 0 && (
        <div className="flex items-center gap-2">
          <label
            htmlFor={`${testIdPrefix}-brand`}
            className="flex items-center gap-1.5 text-xs text-foreground/80 shrink-0"
          >
            <Tag className="w-3.5 h-3.5 text-emerald-300" strokeWidth={2} />
            Brand
          </label>
          <Select
            value={filters.brand || "__all__"}
            onValueChange={(v) =>
              setFilters((f) => ({ ...f, brand: v === "__all__" ? "" : v }))
            }
          >
            <SelectTrigger
              id={`${testIdPrefix}-brand`}
              data-testid={`${testIdPrefix}-brand`}
              className="h-9 rounded-full bg-background border-border text-sm"
            >
              <SelectValue placeholder="All brands" />
            </SelectTrigger>
            <SelectContent className="max-h-80">
              <SelectItem value="__all__" data-testid={`${testIdPrefix}-brand-all`}>
                All brands
              </SelectItem>
              {brandChoices.map(({ name, count }) => (
                <SelectItem
                  key={name}
                  value={name}
                  data-testid={`${testIdPrefix}-brand-${name.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <span className="flex items-center gap-2">
                    {name}
                    {typeof count === "number" && (
                      <span className="text-[10px] text-foreground/50 tabular-nums">
                        {count >= 1000
                          ? `${(count / 1000).toFixed(1)}k`
                          : count}
                      </span>
                    )}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Sliders */}
      <div
        className={`grid ${
          showDistance ? "sm:grid-cols-3" : "sm:grid-cols-2"
        } gap-4 pt-1`}
      >
        <div>
          <div className="flex justify-between text-xs mb-2">
            <span className="text-foreground/80 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-emerald-300" />
              Min THC
            </span>
            <span className="text-emerald-300 font-medium tabular-nums">
              {filters.thc_min || 0}%+
            </span>
          </div>
          <Slider
            data-testid={`${testIdPrefix}-thc`}
            value={[filters.thc_min || 0]}
            onValueChange={([v]) =>
              setFilters((f) => ({ ...f, thc_min: v }))
            }
            max={30}
            step={1}
          />
        </div>
        <div>
          <div className="flex justify-between text-xs mb-2">
            <span className="text-foreground/80">Max price</span>
            <span className="text-emerald-300 font-medium tabular-nums">
              ${filters.price_max || 200}
            </span>
          </div>
          <Slider
            data-testid={`${testIdPrefix}-price`}
            value={[filters.price_max || 200]}
            onValueChange={([v]) =>
              setFilters((f) => ({ ...f, price_max: v }))
            }
            min={10}
            max={200}
            step={5}
          />
        </div>
        {showDistance && (
          <div>
            <div className="flex justify-between text-xs mb-2">
              <span className="text-foreground/80 flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-emerald-300" />
                Distance
              </span>
              <span className="text-emerald-300 font-medium tabular-nums">
                {filters.max_distance_mi || 250} mi
              </span>
            </div>
            <Slider
              data-testid={`${testIdPrefix}-distance`}
              value={[filters.max_distance_mi || 250]}
              onValueChange={([v]) =>
                setFilters((f) => ({ ...f, max_distance_mi: v }))
              }
              min={5}
              max={250}
              step={5}
            />
          </div>
        )}
      </div>

      <label className="flex items-center gap-2.5 text-sm cursor-pointer pt-1 select-none">
        <input
          type="checkbox"
          data-testid={`${testIdPrefix}-in-stock`}
          checked={!!filters.in_stock_only}
          onChange={(e) =>
            setFilters((f) => ({ ...f, in_stock_only: e.target.checked }))
          }
          className="accent-emerald-500 w-4 h-4"
        />
        <span className="text-foreground/85">In stock right now only</span>
      </label>
    </div>
  );
};

export default UnifiedFilters;
